import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import {
  Box,
  Flex,
  Grid,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Center,
  Spinner,
  Tooltip
} from '@chakra-ui/react';

import { Eye, MessageSquare } from 'lucide-react';
import { RepeatIcon, SearchIcon } from '@chakra-ui/icons';
import Pagination from 'react-js-pagination';
import { DateRange } from 'react-date-range';
import { Popover, PopoverTrigger, PopoverContent, PopoverBody } from '@chakra-ui/react';
import { CalendarIcon } from '@chakra-ui/icons';
import { addDays } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { HStack, Menu, MenuButton, MenuList, MenuItem, Icon } from "@chakra-ui/react";
import { ChevronDownIcon, CheckIcon } from "@chakra-ui/icons";
import moment from 'moment';
import { getUserTimeZone } from '../../utils/DefaultFunction';
import usePagePermissions from '../../hooks/usePagePermissions';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import { LuMessageSquare } from 'react-icons/lu';
import PageHeading from '../../components/shared/PageHeading';
const getApiHeaders = () => ({
  'x-api-key': localStorage.getItem('api-key'),
  'x-access-token': localStorage.getItem('customer-portal'),
});

const IncommingSMS = ({ header }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedSMS, setSelectedSMS] = useState(null);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 7),
      key: 'selection',
    },
  ]);
  const [showDateRange, setShowDateRange] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(0);
  const { loading: permissionsLoading, can } = usePagePermissions('incomming-sms');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const limit = 20;
  const API_URL = `${process.env.REACT_APP_API_URL}/incoming-sms`;

  // 🧠 Debounced search
  useEffect(() => {
    if (permissionsLoading || !can('incomming-sms')) return;
    const delayDebounce = setTimeout(() => {
      fetchSMSData(currentPage, searchQuery);
    }, 500);
    return () => clearTimeout(delayDebounce);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsLoading, searchQuery, currentPage]);

  const formatDate = (date) => {
    // force it to local date only, ignoring timezone shifts
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().split('T')[0];
  };


  useEffect(() => {
    if (!autoRefreshInterval) return;

    const interval = setInterval(() => {
      fetchSMSData(
        currentPage,
        searchQuery,
        dateRange[0].startDate,
        dateRange[0].endDate
      );
    }, autoRefreshInterval);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefreshInterval, currentPage, searchQuery, dateRange]);


  const fetchSMSData = async (page = 1, search = '', dateFrom, dateTo) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page,
        limit,
        search,
        date_from: dateFrom ? formatDate(dateFrom) : '',
        date_to: dateTo ? formatDate(dateTo) : '',

      });

      const url = `${API_URL}?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: getApiHeaders(),
      });

      if (!response.ok) throw new Error('Failed to fetch messages');

      const result = await response.json();
      if (result.success && result.data) {
        setData(result.data.items || []);
        setTotalItems(result.data.pagination.total || 0);
      }
    } catch (error) {
      console.error('Error fetching incoming SMS:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleViewDetails = (sms) => {
    setSelectedSMS(sms);
    onOpen();
  };

  const getIntervalLabel = (interval) => {
    switch (interval) {
      case 30000:
        return "30s";
      case 60000:
        return "1m";
      case 300000:
        return "5m";
      default:
        return "OFF";
    }
  };

  const listPermission = can("incomming-sms")
  if (permissionsLoading || loading || !data) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Flex justify="center" align="center" h="300px">
            <Spinner size="lg" />
          </Flex>
        </BoxWrapper>
      </>
    );
  }


  return (
    <>
      <Header header={header} />
      <BoxWrapper>


        {(listPermission) ? (

          <Box className='box-style-card'>
            <PageHeading icon={LuMessageSquare} title="Incoming SMS Messages" subtitle="View incoming SMS messages from your customers" mb={0} />
            <Flex my={4} align="center" justify="space-between" flexWrap="wrap" gap={4}>
              {/* 🔍 Search Input */}
              <InputGroup maxW="400px" flex="1" minW="250px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="#A0AEC0" />
                </InputLeftElement>
                <Input
                  placeholder="Search by phone, message or customer name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg="gray.50"
                  border="1px"
                  borderColor="gray.200"
                  size={"sm"}
                />
              </InputGroup>

              <Flex align="center" gap={2}>
                <Popover
                  isOpen={showDateRange}
                  onClose={() => setShowDateRange(false)}
                  placement="left"
                  closeOnBlur={false}
                >
                  <PopoverTrigger>
                    <Button
                      leftIcon={<CalendarIcon />}
                      bg="var(--brand-primary)"
                      color={"white"}
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDateRange(!showDateRange)}
                    >
                      {dateRange[0].startDate && dateRange[0].endDate
                        ? `${dateRange[0].startDate.toLocaleDateString()} - ${dateRange[0].endDate.toLocaleDateString()}`
                        : 'Select Date Range'}
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent w="auto" p={2} boxShadow="lg" border="1px solid" borderColor="gray.200">
                    <PopoverBody>
                      <DateRange
                        editableDateInputs={true}
                        moveRangeOnFirstSelection={false}
                        ranges={dateRange}
                        onChange={(item) => {
                          setDateRange([item.selection]);

                          // Auto-close when both dates selected
                          if (item.selection.startDate && item.selection.endDate) {
                            setShowDateRange(false);

                            // 🧠 Trigger API call with new date range
                            fetchSMSData(currentPage, searchQuery, item.selection.startDate, item.selection.endDate);
                          }
                        }}
                      />
                    </PopoverBody>
                  </PopoverContent>
                </Popover>


                <HStack spacing={0} borderWidth="1px" borderRadius="md" overflow="hidden">
                  {/* Refresh Button */}
                  <Button
                    size="sm"
                    bg="var(--brand-primary)"
                    borderRightRadius={0}
                    color="white"
                    leftIcon={<RepeatIcon boxSize={3} />}
                    onClick={() =>
                      fetchSMSData(
                        currentPage,
                        searchQuery,
                        dateRange[0].startDate,
                        dateRange[0].endDate
                      )
                    }
                  >
                    Refresh

                  </Button>
                  {/* Auto Refresh Dropdown */}
                  <Menu>
                    <MenuButton
                      as={Button}
                      size="sm"
                      variant="solid"
                      bg="var(--brand-primary)"
                      borderLeftRadius={0}
                      px={2}
                      color={"white"}

                      rightIcon={<ChevronDownIcon boxSize={3} />}
                    >{autoRefreshInterval !== 0 && (
                      <Text ml={2} fontSize="xs" opacity={0.8}>
                        ({getIntervalLabel(autoRefreshInterval)})
                      </Text>
                    )}</MenuButton>
                    <MenuList minW="120px">
                      <MenuItem onClick={() => setAutoRefreshInterval(0)}>
                        OFF {autoRefreshInterval === 0 && <Icon as={CheckIcon} ml="auto" />}
                      </MenuItem>
                      <MenuItem onClick={() => setAutoRefreshInterval(30000)}>
                        Every 30s {autoRefreshInterval === 30000 && <Icon as={CheckIcon} ml="auto" />}
                      </MenuItem>
                      <MenuItem onClick={() => setAutoRefreshInterval(60000)}>
                        Every 1m {autoRefreshInterval === 60000 && <Icon as={CheckIcon} ml="auto" />}
                      </MenuItem>
                      <MenuItem onClick={() => setAutoRefreshInterval(300000)}>
                        Every 5m {autoRefreshInterval === 300000 && <Icon as={CheckIcon} ml="auto" />}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </HStack>

              </Flex>
            </Flex>


            {/* 🧾 Table Data */}
            <Box bg="white" borderRadius="xl" borderWidth="1px" borderColor="gray.200" overflow="hidden">
              {data.length === 0 ? (
                <Text textAlign="center" py={10} fontSize="sm" color="gray.500">No messages found</Text>
              ) : (
                <Table size="sm">
                  <Thead bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                    <Tr>
                      <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Phone Number</Th>
                      <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Message</Th>
                      <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Date</Th>
                      <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em" textAlign="right">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.map((row, i) => (
                      <Tr key={row._id} _hover={{ bg: 'gray.50' }} borderTopWidth={i === 0 ? 0 : '1px'} borderColor="gray.100" transition="background 0.1s">
                        <Td py={4}>
                          <Text fontSize="sm" fontWeight="600" color="gray.800">{row.phone_number}</Text>
                        </Td>
                        <Td py={4} maxW="360px">
                          <Text fontSize="sm" color="gray.600" noOfLines={1}>{row.message_content}</Text>
                        </Td>
                        <Td py={4}>
                          <Text fontSize="sm" color="gray.600">
                            {moment
                              .utc(row.createdAt)
                              .tz(getUserTimeZone())
                              .format("DD MMM YY hh:mm:ss A")}
                          </Text>
                        </Td>
                        <Td py={4} textAlign="right">
                          <Tooltip label="View details" hasArrow fontSize="xs">
                            <IconButton
                              aria-label="View details"
                              icon={<Eye size={14} />}
                              size="xs"
                              variant="ghost"
                              color="gray.500"
                              onClick={() => handleViewDetails(row)}
                            />
                          </Tooltip>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>

            {/* 📄 Pagination */}
            {totalItems > limit && (
              <Flex justify="center" my={6}>
                <Pagination
                  activePage={currentPage}
                  itemsCountPerPage={limit}
                  totalItemsCount={totalItems}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                  innerClass="pagination"
                  itemClass="page-item"
                  linkClass="page-link"
                />
              </Flex>
            )}
          </Box>
        ) : <Box className='card-style'>
          <Center h="400px">
            <Box textAlign="center">
              <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                No Access
              </Text>
              <Text fontSize="sm" color="gray.500">
                You don't have permission to view this sections.
              </Text>
            </Box>
          </Center>
        </Box>}
      </BoxWrapper>


      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader borderBottom="1px" borderColor="gray.200" pb={3}>
            <HStack spacing={2}>
              <MessageSquare size={18} color="var(--brand-primary)" />
              <Text fontSize="md" fontWeight="600">Message Details</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={4}>
            {selectedSMS ? (
              <Box>
                <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="lg" p={3} mb={4}>
                  <Grid templateColumns="repeat(2, 1fr)" rowGap={3} columnGap={6}>
                    <Box>
                      <Text fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" mb={0.5}>
                        Phone Number
                      </Text>
                      <Text fontWeight="600" fontSize="sm" color="gray.800">
                        {selectedSMS.phone_number || '-'}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" mb={0.5}>
                        Response Type
                      </Text>
                      <Text fontWeight="600" fontSize="sm" color="gray.800">
                        {selectedSMS.response_type || '-'}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" mb={0.5}>
                        Received Date
                      </Text>
                      <Text fontWeight="600" fontSize="sm" color="gray.800">
                        {moment
                          .utc(selectedSMS.createdAt)
                          .tz(getUserTimeZone())
                          .format("DD MMM YY hh:mm:ss A")}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" mb={0.5}>
                        Message ID
                      </Text>
                      <Text fontWeight="600" fontSize="sm" color="gray.800" noOfLines={1}>
                        {selectedSMS._id || '-'}
                      </Text>
                    </Box>
                  </Grid>
                </Box>

                {/* Message Content */}
                <Box>
                  <Text fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" mb={1.5}>
                    Message Content
                  </Text>
                  <Box
                    p={3}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderLeft="3px solid"
                    borderLeftColor="var(--brand-primary)"
                    borderRadius="md"
                  >
                    <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                      {selectedSMS.message_content}
                    </Text>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Text>No details available</Text>
            )}
          </ModalBody>

          <ModalFooter borderTop="1px" borderColor="gray.200">
            <Button
              onClick={onClose}
              size="sm"
              bg="var(--brand-primary)"
              color="white"
              _hover={{ bg: 'var(--brand-primary-hover)' }}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </>
  );
};

export default IncommingSMS;
